'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'

import { Button } from '@/components/ui/button'

import { HomeBiNewsStoryRow } from './HomeBiNewsStoryRow'
import type { FooterNewsItem, HomeFooterLoadMoreConfig } from './types'

type Props = HomeFooterLoadMoreConfig & {
  /** Índice do 1º item carregado (continua a numeração da lista inicial → borda correta). */
  startIndex: number
  label?: string
}

/** Endpoint server-side (fora de /api para não colidir com o catch-all do Payload). */
const FOOTER_FEED_ENDPOINT = '/home-feed/footer'

/**
 * Botão "Carregar mais artigos" do home-footer. Reaproveita os MESMOS filtros do bloco
 * (categorias/tags/ordenação/popularidade) — só pagina excluindo o que já foi exibido.
 */
export function HomeFooterLoadMore({
  enabled,
  loadMoreCount,
  excludeIds,
  query,
  startIndex,
  showThumbnail = true,
  label,
}: Props) {
  const t = useTranslations('Home')
  const locale = useLocale()
  const resolvedLabel = label ?? t('loadMoreArticles')
  const [items, setItems] = useState<FooterNewsItem[]>([])
  const [excluded, setExcluded] = useState<number[]>(excludeIds)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  if (!enabled) return null

  const handleClick = async () => {
    if (loading) return
    setLoading(true)
    try {
      const res = await fetch(FOOTER_FEED_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ limit: loadMoreCount, excludeIds: excluded, query, locale }),
      })
      if (!res.ok) {
        setHasMore(false)
        return
      }
      const data = (await res.json()) as {
        items?: FooterNewsItem[]
        ids?: number[]
        hasMore?: boolean
      }
      setItems((prev) => [...prev, ...(data.items ?? [])])
      setExcluded((prev) => [...prev, ...(data.ids ?? [])])
      setHasMore(Boolean(data.hasMore))
    } catch {
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {items.length > 0 ? (
        <div className="flex w-full min-w-0 flex-col">
          {items.map((item, i) => (
            <HomeBiNewsStoryRow
              key={`${item.href}-${startIndex + i}`}
              item={item}
              index={startIndex + i}
              variant="editorial"
              showThumbnail={showThumbnail}
            />
          ))}
        </div>
      ) : null}

      {hasMore ? (
        <div className="mt-6 flex justify-center lg:mt-8">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="min-w-48 rounded-full"
            onClick={handleClick}
            disabled={loading}
            aria-label={resolvedLabel}
          >
            {loading ? t('loading') : resolvedLabel}
          </Button>
        </div>
      ) : null}
    </>
  )
}
